import { useState } from "react";
import { Mail, MapPin, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { usePageMeta } from "@/lib/usePageMeta";
import { PageHeader } from "@/components/site/Content";
import { useCopy } from "@/lib/copy";
import { Rich } from "@/components/site/Rich";

const EMPTY = { name: "", email: "", organisation: "", subject: "", message: "", website: "" };
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const Field = ({ id, label, error, children }) => (
  <div className="flex flex-col gap-2">
    <label htmlFor={id} className="text-sm font-bold text-ink">{label}</label>
    {children}
    {error && <p id={`${id}-error`} className="text-sm font-semibold text-[#B42318]" data-testid={`${id}-error`}>{error}</p>}
  </div>
);

const validate = (f) => {
  const e = {};
  if (!f.name.trim()) e.name = "Please enter your name.";
  if (!EMAIL_RE.test(f.email.trim())) e.email = "Please enter a valid email address.";
  if (!f.subject.trim()) e.subject = "Please add a subject.";
  if (f.message.trim().length < 10) e.message = "Please write a message of at least 10 characters.";
  return e;
};

const ContactDetails = ({ t, email }) => (
  <div className="lg:col-span-4">
    <h2 className="text-xl font-extrabold text-brand" data-testid="contact-details-heading">{t("contact.details.heading")}</h2>
    <dl className="mt-6 divide-y divide-line border-y border-line">
      <div className="flex gap-4 py-5" data-testid="contact-email">
        <Mail className="mt-0.5 h-5 w-5 text-brand" aria-hidden="true" />
        <div><dt className="eyebrow text-ink-muted">Email</dt><dd className="mt-1 font-semibold"><a href={`mailto:${email}`} className="text-brand underline underline-offset-4" data-testid="contact-email-link">{email}</a></dd></div>
      </div>
      <div className="flex gap-4 py-5" data-testid="contact-address">
        <MapPin className="mt-0.5 h-5 w-5 text-brand" aria-hidden="true" />
        <div><dt className="eyebrow text-ink-muted">Address</dt><dd className="mt-1 font-semibold not-italic text-ink"><address className="whitespace-pre-line not-italic" data-testid="contact-address-text">{t("contact.details.address")}</address></dd></div>
      </div>
    </dl>
  </div>
);

export default function Contact() {
  usePageMeta("Contact BRaIN", "Contact the Bengaluru Research and Innovation Network (BRaIN) to collaborate on research and innovation in Bengaluru.");
  const t = useCopy();
  const email = t("contact.details.email");
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [state, setState] = useState({ busy: false, sent: false, error: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (state.busy) return;
    const v = validate(form);
    setErrors(v);
    if (Object.keys(v).length) return;
    setState({ busy: true, sent: false, error: "" });
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, form, { timeout: 30000 });
      setState({ busy: false, sent: true, error: "" });
      setForm(EMPTY);
    } catch (err) {
      const d = err.response?.data?.detail;
      setState({ busy: false, sent: false, error: typeof d === "string" ? d : `Sorry, your message could not be sent. Please try again, or email ${email} directly.` });
    }
  };

  const input = (k, props = {}) => (
    <input id={`c-${k}`} value={form[k]} onChange={set(k)} className="field" aria-invalid={Boolean(errors[k])} aria-describedby={errors[k] ? `c-${k}-error` : undefined} data-testid={`contact-${k}-input`} {...props} />
  );

  return (
    <>
      <PageHeader eyebrow={t("contact.header.eyebrow")} title={t("contact.header.title")} intro={<Rich text={t("contact.header.intro")} />} />
      <section className="container-x grid gap-12 py-14 md:py-20 lg:grid-cols-12">
        <ContactDetails t={t} email={email} />
        <form className="border border-line p-6 md:p-10 lg:col-span-8" onSubmit={submit} noValidate data-testid="contact-form">
          <h2 className="text-xl font-extrabold text-brand" data-testid="contact-form-heading">{t("contact.form.heading")}</h2>
          {state.sent && (
            <p role="status" className="mt-6 flex items-start gap-2 border border-brand-green bg-pale p-4 font-semibold text-ink" data-testid="contact-success">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand" aria-hidden="true" /> <span className="whitespace-pre-line">{t("contact.form.success")}</span>
            </p>
          )}
          <div className="mt-8 grid gap-6 md:grid-cols-2">
            <Field id="c-name" label="Full name *" error={errors.name}>{input("name", { autoComplete: "name", maxLength: 120 })}</Field>
            <Field id="c-email" label="Email address *" error={errors.email}>{input("email", { type: "email", autoComplete: "email", maxLength: 254 })}</Field>
            <Field id="c-organisation" label="Organisation (optional)">{input("organisation", { autoComplete: "organization", maxLength: 200 })}</Field>
            <Field id="c-subject" label="Subject *" error={errors.subject}>{input("subject", { maxLength: 200 })}</Field>
            <div className="md:col-span-2">
              <Field id="c-message" label="Message *" error={errors.message}>
                <textarea id="c-message" rows={6} value={form.message} onChange={set("message")} maxLength={5000} className="field resize-y" aria-invalid={Boolean(errors.message)} aria-describedby={errors.message ? "c-message-error" : undefined} data-testid="contact-message-input" />
              </Field>
            </div>
            <div className="hidden" aria-hidden="true">
              <label htmlFor="c-website">Website</label>
              <input id="c-website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
            </div>
          </div>
          {state.error && <p role="alert" className="mt-6 font-semibold text-[#B42318]" data-testid="contact-error">{state.error}</p>}
          <button type="submit" disabled={state.busy} className="mt-8 rounded-full bg-brand px-7 py-3 text-sm font-bold text-white transition-colors hover:bg-[#0B3D8A] disabled:opacity-60" data-testid="contact-submit-button">
            {state.busy ? "Sending…" : t("contact.form.submit")}
          </button>
        </form>
      </section>
    </>
  );
}
